import React, { useEffect, useState, useRef } from "react";
import { useAudio } from "../providers/AudioProvider";
import { useSettings } from "../providers/SettingsContext";

interface AudioControlProps {
  id: string;
  audioBlob: Blob | null;
  isLoading?: boolean;
  autoPlay?: boolean;
}

const AudioControl: React.FC<AudioControlProps> = ({
  id,
  audioBlob,
  isLoading = false,
  autoPlay = false,
}) => {
  const { settings } = useSettings();
  const audio = useAudio();
  const [mediaState, setMediaState] = useState(() =>
    audio.mediaState.select(id)
  );
  const previousIdRef = useRef<string>(id);

  useEffect(() => {
    const unsubscribe = audio.mediaState.subscribe(() => {
      setMediaState(audio.mediaState.select(id));
    });
    // subscribes to media state changes
    // returns function that can be called to unsubscribe
    // unsubscribe called when component umounts

    return unsubscribe;
  }, [id, audio.mediaState]);

  // Pause audio when id changes (different audio source)
  useEffect(() => {
    const previousId = previousIdRef.current;
    if (previousId !== id) {
      const currentState = audio.mediaState.getState();
      if (
        currentState.currentId === previousId &&
        currentState.status === "playing"
      ) {
        audio.pause(previousId);
      }
      // Update mediaState to reflect the new id immediately
      setMediaState(audio.mediaState.select(id));
      previousIdRef.current = id;

      // If autoPlay is enabled, play the new audio
      if (autoPlay && audioBlob) {
        setTimeout(() => {
          audio.play(id);
        }, 100);
      }
    }
  }, [id, audio, autoPlay, audioBlob]);

  // Register audio source when blob arrives
  useEffect(() => {
    if (!audioBlob) return;
    audio.registerAudioSource(id, audioBlob);
  }, [id, audioBlob, autoPlay, audio]);

  useEffect(() => {
    if (autoPlay && audioBlob) {
      setTimeout(() => {
        audio.play(id);
      }, 100);
    }
  }, []);

  // Cleanup: pause audio when component unmounts (e.g., modal closes)
  useEffect(() => {
    return () => {
      const currentState = audio.mediaState.getState();
      if (currentState.currentId === id && currentState.status === "playing") {
        audio.pause(id);
      }
    };
  }, [id, audio]);

  // Determine if audio is playing from centralized state
  const isPlaying = mediaState.isCurrent && mediaState.status === "playing";

  const toggle = async () => {
    if (isPlaying) {
      audio.pause(id);
    } else {
      audio.play(id);
    }
  };

  // Calculate icon size based on text size setting
  const iconSize = Math.max(16, Math.min(30, settings.textSize * 0.8));
  const buttonSize = Math.max(32, Math.min(48, settings.textSize * 2));

  if (isLoading) {
    return (
      <button
        className="flex items-center justify-center rounded-full bg-gray-300 text-gray-600 hover:bg-gray-400 transition-colors"
        style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
        disabled
      >
        <div
          className="border-2 border-gray-600 border-t-transparent rounded-full animate-spin"
          style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
        ></div>
      </button>
    );
  }

  if (!audioBlob) {
    return null;
  }

  return (
    <button
      className="rounded-full border-none bg-transparent cursor-pointer text-black flex items-center justify-center p-1 transition-all duration-300 hover:bg-gray-400"
      style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
      onClick={toggle}
      data-trust-trigger="audio-control"
      aria-label={isPlaying ? "Pause audio" : "Play audio"}
      title={isPlaying ? "Pause audio" : "Play audio"}
    >
      {isPlaying ? (
        // Pause SVG
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="4" y="3" width="4" height="14" rx="1" fill="currentColor" />
          <rect x="12" y="3" width="4" height="14" rx="1" fill="currentColor" />
        </svg>
      ) : (
        // Play SVG
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polygon points="5,3 17,10 5,17" fill="currentColor" />
        </svg>
      )}
    </button>
  );
};

export default AudioControl;
